import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  PasswordInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi } from './platformApi';
import { ImageUploadInput } from '../../components/ImageUploadInput';

export interface PlatformTenant {
  tenantId: string;
  slug: string;
  businessNameAr: string;
  vertical: number;
  status: number;
  planCode: string;
  createdAt: string;
  planModuleCodes: string[];
  extraModuleCodes: string[];
  customDomain: string | null;
  logoUrl: string | null;
  aiApiKey: string | null;
}

interface PlatformModule {
  code: string;
  nameAr: string;
  isCore: boolean;
}

interface PlatformPlan {
  code: string;
  nameAr: string;
}

const STATUS_COLOR: Record<number, string> = { 1: 'yellow', 2: 'green', 3: 'red', 4: 'gray' };

export function PlatformTenantsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [modulesFor, setModulesFor] = useState<PlatformTenant | null>(null);
  const [profileFor, setProfileFor] = useState<PlatformTenant | null>(null);

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['platform-tenants'] as const,
    queryFn: async () =>
      (await platformApi.get<PlatformTenant[]>('/api/platform/tenants')).data,
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: number }) =>
      platformApi.put(`/api/platform/tenants/${id}/status`, { status }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform-tenants'] }),
  });

  const statusLabel = (s: number) =>
    t(`platform.tenants.status.${s === 1 ? 'trial' : s === 2 ? 'active' : s === 3 ? 'suspended' : 'cancelled'}`);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          {t('platform.tenants.title')}
        </Text>
        <Button
          color="wheels"
          leftSection={<IconPlus size={16} stroke={1.8} />}
          onClick={() => setOnboardOpen(true)}
        >
          {t('platform.tenants.onboard')}
        </Button>
      </Group>

      <Paper p="md">
        {isLoading ? (
          <Group justify="center" py="lg">
            <Loader color="wheelsRed" />
          </Group>
        ) : (
          <Table verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('platform.tenants.business')}</Table.Th>
                <Table.Th>{t('platform.tenants.slug')}</Table.Th>
                <Table.Th>{t('platform.tenants.plan')}</Table.Th>
                <Table.Th>{t('platform.tenants.modules')}</Table.Th>
                <Table.Th>{t('platform.tenants.statusCol')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(tenants ?? []).map((tenant) => (
                <Table.Tr key={tenant.tenantId}>
                  <Table.Td>{tenant.businessNameAr}</Table.Td>
                  <Table.Td dir="ltr" className="font-mono">
                    {tenant.slug}
                  </Table.Td>
                  <Table.Td>{tenant.planCode}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <Badge variant="light" color="gray" radius="sm">
                        {tenant.planModuleCodes.length + tenant.extraModuleCodes.length}
                      </Badge>
                      {tenant.extraModuleCodes.slice(0, 3).map((code) => (
                        <Badge key={code} variant="light" color="wheels" radius="sm">
                          {code}
                        </Badge>
                      ))}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[tenant.status] ?? 'gray'} variant="light">
                      {statusLabel(tenant.status)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end">
                      <Button size="xs" variant="default" onClick={() => setModulesFor(tenant)}>
                        {t('platform.tenants.editModules')}
                      </Button>
                      <Button size="xs" variant="default" onClick={() => setProfileFor(tenant)}>
                        {t('platform.tenants.profile')}
                      </Button>
                      <Button
                        size="xs"
                        variant="subtle"
                        color={tenant.status === 3 ? 'green' : 'red'}
                        loading={statusMutation.isPending}
                        onClick={() =>
                          statusMutation.mutate({
                            id: tenant.tenantId,
                            status: tenant.status === 3 ? 2 : 3,
                          })
                        }
                      >
                        {tenant.status === 3
                          ? t('platform.tenants.activate')
                          : t('platform.tenants.suspend')}
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <OnboardModal opened={onboardOpen} onClose={() => setOnboardOpen(false)} />
      <ModulesModal tenant={modulesFor} onClose={() => setModulesFor(null)} />
      <ProfileModal tenant={profileFor} onClose={() => setProfileFor(null)} />
    </Stack>
  );
}

export function OnboardModal({
  opened,
  onClose,
  leadId,
}: {
  opened: boolean;
  onClose: () => void;
  /** When set, onboarding goes through the CRM convert endpoint. */
  leadId?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    slug: '',
    businessNameAr: '',
    vertical: '1',
    planCode: '',
    ownerEmail: '',
    ownerPassword: '',
    ownerNameAr: '',
  });

  const { data: plans } = useQuery({
    queryKey: ['platform-plans'] as const,
    queryFn: async () => (await platformApi.get<PlatformPlan[]>('/api/platform/plans')).data,
    enabled: opened,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const tenant = {
        slug: form.slug,
        businessNameAr: form.businessNameAr,
        businessNameEn: null,
        vertical: Number(form.vertical),
        planCode: form.planCode,
        ownerEmail: form.ownerEmail,
        ownerPassword: form.ownerPassword,
        ownerNameAr: form.ownerNameAr,
        branchNameAr: null,
      };
      if (leadId) {
        return platformApi.post(`/api/platform/crm/leads/${leadId}/convert`, { tenant });
      }
      return platformApi.post('/api/platform/tenants', tenant);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-crm'] });
      notifications.show({ color: 'green', title: t('platform.tenants.onboarded'), message: form.slug });
      onClose();
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: t('platform.tenants.onboardFailed'),
        message: err instanceof Error ? err.message : '',
      });
    },
  });

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <Modal opened={opened} onClose={onClose} title={t('platform.tenants.onboard')} size="lg">
      <Stack gap="sm">
        <Group grow>
          <TextInput
            label={t('platform.tenants.business')}
            value={form.businessNameAr}
            onChange={(e) => set('businessNameAr')(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('platform.tenants.slug')}
            description="a-z, 0-9, -"
            value={form.slug}
            onChange={(e) => set('slug')(e.currentTarget.value.toLowerCase())}
            dir="ltr"
            required
          />
        </Group>
        <Group grow>
          <Select
            label={t('platform.tenants.vertical')}
            value={form.vertical}
            onChange={(v) => set('vertical')(v ?? '1')}
            data={[
              { value: '1', label: t('platform.verticals.qsr') },
              { value: '2', label: t('platform.verticals.restaurant') },
              { value: '3', label: t('platform.verticals.supermarket') },
            ]}
          />
          <Select
            label={t('platform.tenants.plan')}
            value={form.planCode || null}
            onChange={(v) => set('planCode')(v ?? '')}
            data={(plans ?? []).map((p) => ({ value: p.code, label: `${p.nameAr} (${p.code})` }))}
            required
          />
        </Group>
        <Group grow>
          <TextInput
            label={t('platform.tenants.ownerName')}
            value={form.ownerNameAr}
            onChange={(e) => set('ownerNameAr')(e.currentTarget.value)}
            required
          />
          <TextInput
            label={t('platform.tenants.ownerEmail')}
            value={form.ownerEmail}
            onChange={(e) => set('ownerEmail')(e.currentTarget.value)}
            dir="ltr"
            required
          />
        </Group>
        <PasswordInput
          label={t('platform.tenants.ownerPassword')}
          description={t('platform.tenants.ownerPasswordHint')}
          value={form.ownerPassword}
          onChange={(e) => set('ownerPassword')(e.currentTarget.value)}
          required
        />
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            color="wheels"
            loading={mutation.isPending}
            disabled={
              !form.slug || !form.businessNameAr || !form.planCode
              || !form.ownerEmail || form.ownerPassword.length < 10 || !form.ownerNameAr
            }
            onClick={() => mutation.mutate()}
          >
            {t('platform.tenants.onboardSubmit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ModulesModal({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<string[] | null>(null);

  const { data: modules } = useQuery({
    queryKey: ['platform-modules'] as const,
    queryFn: async () => (await platformApi.get<PlatformModule[]>('/api/platform/modules')).data,
    enabled: tenant !== null,
  });

  const mutation = useMutation({
    mutationFn: async () =>
      platformApi.put(`/api/platform/tenants/${tenant!.tenantId}/modules`, {
        moduleCodes: selection ?? tenant!.extraModuleCodes,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      onClose();
      setSelection(null);
    },
  });

  const planSet = new Set(tenant?.planModuleCodes ?? []);
  const options = (modules ?? [])
    .filter((m) => !m.isCore && !planSet.has(m.code))
    .map((m) => ({ value: m.code, label: `${m.nameAr} (${m.code})` }));

  return (
    <Modal
      opened={tenant !== null}
      onClose={() => {
        onClose();
        setSelection(null);
      }}
      title={`${t('platform.tenants.editModules')} — ${tenant?.businessNameAr ?? ''}`}
    >
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t('platform.tenants.planIncludes')}: {tenant?.planModuleCodes.join('، ')}
        </Text>
        <MultiSelect
          label={t('platform.tenants.extraModules')}
          value={selection ?? tenant?.extraModuleCodes ?? []}
          onChange={setSelection}
          data={options}
          searchable
        />
        <Group justify="flex-end">
          <Button color="wheels" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ProfileModal({
  tenant,
  onClose,
}: {
  tenant: PlatformTenant | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState<string | null>(null);
  const [logo, setLogo] = useState<string | null>(null);
  const [aiKey, setAiKey] = useState<string | null>(null);

  const domainValue = domain ?? tenant?.customDomain ?? '';
  const logoValue = logo ?? tenant?.logoUrl ?? '';
  const aiKeyValue = aiKey ?? tenant?.aiApiKey ?? '';

  const mutation = useMutation({
    mutationFn: async () =>
      platformApi.put(`/api/platform/tenants/${tenant!.tenantId}/profile`, {
        customDomain: domainValue.trim() || null,
        logoUrl: logoValue.trim() || null,
        aiApiKey: aiKeyValue.trim() || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      onClose();
      setDomain(null);
      setLogo(null);
      setAiKey(null);
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: t('platform.tenants.profileFailed'),
        message: err instanceof Error ? err.message : '',
      });
    },
  });

  // Logo auto-save: persists right after upload/remove (modal stays open).
  // Saves ONLY the logo — domain/AI-key edits still go through the Save
  // button, using the tenant's STORED values here so unsaved edits aren't
  // committed as a side effect.
  const autoSaveLogo = useMutation({
    mutationFn: async (url: string | null) =>
      platformApi.put(`/api/platform/tenants/${tenant!.tenantId}/profile`, {
        customDomain: tenant?.customDomain ?? null,
        logoUrl: url,
        aiApiKey: tenant?.aiApiKey ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform-tenants'] });
      notifications.show({ color: 'green', title: t('common.toasts.updated'), message: '' });
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        title: t('platform.tenants.profileFailed'),
        message: err instanceof Error ? err.message : '',
      });
    },
  });

  return (
    <Modal
      opened={tenant !== null}
      onClose={() => { onClose(); setDomain(null); setLogo(null); setAiKey(null); }}
      title={`${t('platform.tenants.profile')} — ${tenant?.businessNameAr ?? ''}`}
    >
      <Stack gap="sm">
        <TextInput
          label={t('platform.tenants.customDomainLabel')}
          description={t('platform.tenants.customDomainHelp')}
          placeholder="pos.acme.com"
          dir="ltr"
          value={domainValue}
          onChange={(e) => setDomain(e.currentTarget.value)}
        />
        <ImageUploadInput
          label={t('platform.tenants.logoLabel')}
          api={platformApi}
          uploadPath={tenant ? `/api/platform/tenants/${tenant.tenantId}/logo-image` : undefined}
          value={logoValue.trim() || null}
          onChange={(url) => {
            setLogo(url ?? '');
            autoSaveLogo.mutate(url);
          }}
          disabled={tenant === null}
        />
        <TextInput
          label={t('platform.tenants.aiKeyLabel')}
          description={t('platform.tenants.aiKeyHelp')}
          placeholder="sk-ant-…"
          dir="ltr"
          value={aiKeyValue}
          onChange={(e) => setAiKey(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button color="wheels" loading={mutation.isPending} onClick={() => mutation.mutate()}>
            {t('common.save')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
